import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ApiService } from "~/app/core/api.service";
import { NavigationService } from "~/app/core/navigation.service";
import { OfferSearch } from "~/app/core/model/offer-search";
import { InteractionType } from "~/app/core/model/interaction-type";
import { InteractionComponent } from "~/app/core/model/interaction-component";
import { BasePage } from "~/app/pages/base.page";
import { NotificationService } from "~/app/core/notification.service";
import { NetworkConnectionType, NetworkService } from "~/app/core/network.service";
import * as Log from "~/app/core/log";
import { ActivatedRoute } from "@angular/router";
import { InteractionTimer } from "~/app/core/interaction.service";
import {CoreService} from "~/app/core/core.service";

@Component({
    selector: "Location",
    templateUrl: "./location.page.html",
    styleUrls: ["./location.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationPage extends BasePage implements OnInit, OnDestroy {
    initialSearchRadius: number = null;

    searchGPS: boolean = false;
    searchZipCode: string = null;
    searchRadius: number = null;
    allowLocation: boolean = null;
    showLocationSettingsButton: "none" | "location" = "none";

    get readyToSearch(): boolean {
        const ready = this.searchGPS || (this.searchZipCode !== null && this.searchZipCode > '0');
        return ready;
    }

    private alternateLocations: Array<{label: string, zip: string}>;
    private readonly userId: number;
    private forceSearch: boolean = false;
    private pageTimer: InteractionTimer;

    constructor(
        private readonly core: CoreService,
        private readonly route: ActivatedRoute,
        private readonly cd: ChangeDetectorRef
    ) {
        super();
        const userInfo = this.core.api.user_info();
        this.userId = userInfo ? userInfo.id : null;
    }

    ngOnInit(): void {
        Log.debug(Log.Tag.LocationPage, "allowBack: " + this.route.snapshot.queryParamMap.get("allowBack"));
        this.core.api.interactOther({ pgLocationCount: 1 });
        this.pageTimer = this.core.api.interactTimerOther("pgLocationTime");
        this.allowBack = this.route.snapshot.queryParamMap.has("allowBack") && String(this.route.snapshot.queryParamMap.get("allowBack")) === "true";

        const search: OfferSearch = this.core.api.offers_getSearch();
        if (search) {
            this.searchGPS = !!search.location;
            this.searchZipCode = search.zipCode;
            this.searchRadius = search.radius;
            this.updateSearchRadius(this.searchRadius);
            this.initialSearchRadius = this.searchRadius;
        }

        // Init your component properties here.
        this.core.api.location_isAllowLocation().then((result) => {
            this.allowLocation = result;
            if (!this.allowLocation) {
                this.searchGPS = false;
            }
        }, (error) => {
            this.searchGPS = false;
        });
    }

    ngOnDestroy() {
        this.pageTimer.log();
    }

    goBack() {
        if (!this.waiting) {
            this.waiting = true;
            this.waitingSpinning = false;
            if (this.forceSearch) {
                this.core.nav.navPageSearching(this.core.api);
            } else {
                this.core.nav.navBack();
            }
        }
    }

    onChangeSearchGPS(event: boolean): void {
        if (this.searchGPS !== event) {
            if (this.searchGPS === true) {
                this.searchGPS = false;
                this.core.api.offers_setSearch({
                    location: this.searchGPS,
                    zipCode: this.searchZipCode,
                    radius: this.searchRadius
                });
            } else {
                if (!this.allowLocation) {
                    this.core.api.location_requestLocationPermission().then((result) => {
                        if (!result) {
                            this.core.api.interaction(InteractionType.RejectLocation, InteractionComponent.LocationPage);
                            this.core.toast.toastError({
                                header: "Location Unavailable",
                                message: "We're unable to access your device's location.\n\nYou'll need to enable the Location permission from the Settings app."
                            }, 1000);
                        } else {
                            this.core.api.interaction(InteractionType.AllowLocation, InteractionComponent.LocationPage);
                        }
                        this.allowLocation = result;
                        this.searchGPS = this.allowLocation;
                        this.forceSearch = true;
                        this.core.api.offers_setSearch({
                            location: this.searchGPS,
                            zipCode: this.searchZipCode,
                            radius: this.searchRadius
                        });
                        this.cd.detectChanges();
                    });
                } else {
                    this.searchGPS = true;
                    this.forceSearch = true;
                    this.core.api.offers_setSearch({
                        location: this.searchGPS,
                        zipCode: this.searchZipCode,
                        radius: this.searchRadius
                    });
                    this.cd.detectChanges();
                }
            }
        }
    }

    onLaunchSettingsApp() {
        this.showLocationSettingsButton = "none";
        this.core.nav.launchSettingsApp();
    }

    onChangeSearchZipCode(event: string): void {
        const z: number = event.length > 0 ? Number(event.replace(/\D/g, "")) : null;
        if (z.toString() !== this.searchZipCode) {
            this.searchZipCode = z.toString();
            this.forceSearch = true;
            this.core.api.offers_setSearch({
                location: this.searchGPS,
                zipCode: z.toString(),
                radius: this.searchRadius
            });
        }
    }

    onCitiesTap() {
        if (!this.alternateLocations) {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.waiting = false;
                this.cd.detectChanges();
                this.core.toast.toastError({
                    header: "No Data",
                    message: "Sorry! We can't lookup locations with FREE offers without data or wi-fi available.\n\nPlease enable data and try again."
                });
            } else {
                const errorHandler = (error) => {
                    this.core.toast.toastError({
                        header: "Problem Finding Cities",
                        message: "We're unable to lookup cities with offers. Try again later."
                    });
                    this.waiting = false;
                    this.cd.detectChanges();
                };
                this.waiting = true;
                this.cd.detectChanges();
                const limit = this.core.api.device_loadSettings().testing ? 100 : 10;
                this.core.api.offers_alternateLocations({zipCode: null, location: true, radius: 250}, limit).then((locations) => {
                    this.waiting = false;
                    this.cd.detectChanges();
                    this.alternateLocations = locations.locations;
                    this.promptForCity();
                }, errorHandler
                ).catch(errorHandler);
            }
        } else {
            this.promptForCity();
        }
    }

    onChangeSearchRadius(event: any): void {
        const z: number = event.value.length > 0 ? Number(event.value) : null;
        if (z !== this.searchRadius) {
            this.updateSearchRadius(z);
        }
    }

    onSearchTap() {
        if (this.readyToSearch) {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.waiting = false;
                this.cd.detectChanges();
                this.core.toast.toastError({
                    header: "No Data",
                    message: "Sorry! We can't find FREE offers without data or wi-fi available.\n\nPlease enable data and try again."
                });
            } else {
                this.core.api.offers_setSearch({
                    location: this.searchGPS,
                    zipCode: this.searchZipCode,
                    radius: this.searchRadius
                });
                this.core.nav.navPageSearching(this.core.api);
            }
        } else {
            this.core.toast.toastError({
                header: "Missing Search Criteria",
                message: "Whoops! You'll need to either enable GPS or enter a zip code in order to find FREE offers."
            });
        }
    }

    private promptForCity() {
        const actionList = [];
        this.alternateLocations.forEach((loc) => {
            actionList.push(loc.label);
        });
        this.core.dialogs.choose({
            message: "Select a city to use its zip code",
            buttons: actionList
        }).then((result) => {
            this.waiting = false;
            this.cd.detectChanges();
            if (result) {
                this.alternateLocations.filter((loc) => loc.label === result).forEach((loc) => {
                    this.searchZipCode = loc.zip;
                    this.forceSearch = true;
                    this.core.api.offers_setSearch({
                        location: this.searchGPS,
                        zipCode: this.searchZipCode,
                        radius: this.searchRadius
                    });
                    this.core.nav.navPageSearching();
                });
            }
        });
    }

    private updateSearchRadius(newRadius: number) {
        if (newRadius) {
            if (newRadius < 10) {
                newRadius = 10;
                if (this.searchRadius !== 10) {
                    this.core.toast.toastError({
                        header: "Search Radius",
                        message: "The minimum search radius is 10 miles."
                    });
                }
            } else if (newRadius > 250) {
                newRadius = 250;
                if (this.searchRadius !== 250) {
                    this.core.toast.toastError({
                        header: "Search Radius",
                        message: "The maximum search radius is 250 miles."
                    });
                }
            }
        }

        if (newRadius && newRadius !== this.searchRadius) {
            this.searchRadius = newRadius;
            this.forceSearch = true;
            this.core.api.offers_setSearch({
                location: this.searchGPS,
                zipCode: this.searchZipCode,
                radius: this.searchRadius
            });
        }
    }
}
